import { http, HttpResponse, delay, type HttpHandler } from 'msw';
import { setupWorker, type SetupWorker } from 'msw/browser';
import { loadState, updateState } from '../../storage';
import type { DynamicMockDefinition, HttpMethod } from './msw-mocks.types';

let worker: SetupWorker | null = null;

const METHOD_MAP: Record<HttpMethod, typeof http.get> = {
  GET: http.get,
  POST: http.post,
  PUT: http.put,
  PATCH: http.patch,
  DELETE: http.delete,
};

function buildHandler(def: DynamicMockDefinition): HttpHandler {
  const method = METHOD_MAP[def.method];
  return method(def.urlPattern, async () => {
    if (def.delayMs > 0) {
      await delay(def.delayMs);
    }

    if (def.errorType === 'cors') {
      console.group('ACME Dev Tools');
      console.error(
        `Access to fetch at '${def.urlPattern}' from origin '${location.origin}' has been blocked by CORS policy: No 'Access-Control-Allow-Origin' header is present on the requested resource.`,
      );
      console.groupEnd();
      return HttpResponse.error();
    }

    if (def.errorType === 'network') {
      return HttpResponse.error();
    }

    if (def.contentType === 'json') {
      return HttpResponse.json(JSON.parse(def.responseBody), { status: def.statusCode });
    }

    return new HttpResponse(def.responseBody, { status: def.statusCode });
  });
}

export function rebuildRuntimeHandlers(): void {
  window.dispatchEvent(new CustomEvent('acme:devtools-badge-update'));

  if (!worker) {
    return;
  }

  const { definitions } = loadState().mswMocks;
  worker.resetHandlers();
  const enabled = definitions.filter((d) => d.enabled);

  if (enabled.length > 0) {
    worker.use(...enabled.map(buildHandler));
  }
}

export function getWorker(): SetupWorker | null {
  return worker;
}

export function getMocks(): DynamicMockDefinition[] {
  return loadState().mswMocks.definitions;
}

export function addMock(def: Omit<DynamicMockDefinition, 'id' | 'createdAt'>): DynamicMockDefinition {
  const newDef: DynamicMockDefinition = {
    ...def,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  };

  updateState((s) => {
    s.mswMocks.definitions.push(newDef);
  });

  rebuildRuntimeHandlers();
  return newDef;
}

export function removeMock(id: string): void {
  updateState((s) => {
    s.mswMocks.definitions = s.mswMocks.definitions.filter((d) => d.id !== id);
  });

  rebuildRuntimeHandlers();
}

export function toggleMock(id: string): void {
  updateState((s) => {
    const def = s.mswMocks.definitions.find((d) => d.id === id);
    if (def) {
      def.enabled = !def.enabled;
    }
  });

  rebuildRuntimeHandlers();
}

export function updateMock(id: string, partial: Partial<Omit<DynamicMockDefinition, 'id' | 'createdAt'>>): void {
  updateState((s) => {
    const def = s.mswMocks.definitions.find((d) => d.id === id);
    if (def) {
      Object.assign(def, partial);
    }
  });

  rebuildRuntimeHandlers();
}

export function clearMocks(): void {
  updateState((s) => {
    s.mswMocks.definitions = [];
  });

  window.dispatchEvent(new CustomEvent('acme:devtools-badge-update'));

  if (worker) {
    worker.resetHandlers();
  }
}

export async function initMswWorker(): Promise<void> {
  worker = setupWorker();
  rebuildRuntimeHandlers();
  await worker.start({ onUnhandledRequest: 'bypass' });
}
